/**
 * Where a merchant is, for the sending window (PLAN.md §4.6; owner decisions 2026-10-12, questions 6–9).
 * Pure: the pacer and the web app call the same function with the same reference data.
 *
 *  1. A number outside the US (another country, Canada, the Caribbean, a US territory) is blocked: other
 *     countries' rules (Canada: CASL and the CRTC rules) are a separate project.
 *  2. The business address state and the phone's area code are BOTH respected. When they point to
 *     different places (a business that moved, a ported cell phone), every zone of both counts and both
 *     states' rules apply, so the stricter one wins.
 *  3. An area code inside one zone of a two-zone state narrows the state's zones; otherwise the state's
 *     zones all count (the window is their overlap).
 *  4. An admin may pick one zone by hand, but only one the state or the area code already allows. A
 *     manual zone that no longer fits (the state changed since) is ignored.
 *  5. No state and no US area code (toll-free and the like): the text waits until someone adds the
 *     merchant's state. Nothing is guessed.
 */

export type AreaCodeRegion =
  'us_state' | 'us_territory' | 'canada' | 'other_country' | 'non_geographic';

export interface AreaCodeInfo {
  region: AreaCodeRegion;
  /** The US state (only for us_state). */
  state: string | null;
  /** The one zone of a code inside one zone of a multi-zone state; null = the state's zones. */
  timezone: string | null;
}

export interface LocationData {
  areaCodes: ReadonlyMap<string, AreaCodeInfo>;
  /** IANA zones per state (state_rules.timezones), 50 states + DC. */
  stateZones: ReadonlyMap<string, readonly string[]>;
}

export interface MerchantLocationInput {
  phone: string;
  /** The business address state (merchants.state), or null. */
  state: string | null;
  /** merchants.timezone when merchants.timezone_source = 'manual', else null. */
  manualZone: string | null;
}

export type LocationSource = 'state' | 'area_code' | 'state_and_area_code' | 'manual';

export type MerchantLocation =
  | {
      ok: true;
      /** Every candidate zone: the window must be open in all of them. */
      zones: string[];
      /** Every state whose rules apply. */
      states: string[];
      source: LocationSource;
      /** The address state and the area code point to different states. */
      mismatch: boolean;
      /** The zones an admin may pick by hand. */
      candidates: string[];
      /** A manual zone was set but no longer fits the state and area code. */
      manualIgnored: boolean;
    }
  | { ok: false; action: 'block'; reason: 'outside_us' }
  | { ok: false; action: 'hold'; reason: 'timezone_unknown'; candidates: string[] };

/** Area code of a NANP number (+1NXX…), or null for any other number. */
export function nanpAreaCode(e164: string): string | null {
  return /^\+1([2-9]\d{2})\d{7}$/.exec(e164)?.[1] ?? null;
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)].sort();
}

export function resolveLocation(
  merchant: MerchantLocationInput,
  data: LocationData,
): MerchantLocation {
  const areaCode = nanpAreaCode(merchant.phone);
  if (!areaCode) return { ok: false, action: 'block', reason: 'outside_us' };
  const code = data.areaCodes.get(areaCode);
  if (code && code.region !== 'us_state' && code.region !== 'non_geographic') {
    return { ok: false, action: 'block', reason: 'outside_us' };
  }

  // A state we do not know (not one of the 50 + DC) is treated as no state: never guessed from.
  const state = merchant.state && data.stateZones.has(merchant.state) ? merchant.state : null;
  const stateZones = state ? [...(data.stateZones.get(state) ?? [])] : [];

  const codeState = code?.region === 'us_state' ? code.state : null;
  const codeZones = codeState
    ? code?.timezone
      ? [code.timezone]
      : [...(data.stateZones.get(codeState) ?? [])]
    : [];

  const candidates = unique([...stateZones, ...codeZones]);
  if (candidates.length === 0) {
    return { ok: false, action: 'hold', reason: 'timezone_unknown', candidates: [] };
  }

  const states = unique([state, codeState].filter((s): s is string => s !== null));
  const mismatch = state !== null && codeState !== null && state !== codeState;

  if (merchant.manualZone) {
    if (candidates.includes(merchant.manualZone)) {
      return {
        ok: true,
        zones: [merchant.manualZone],
        states,
        source: 'manual',
        mismatch,
        candidates,
        manualIgnored: false,
      };
    }
  }
  const manualIgnored = merchant.manualZone !== null && merchant.manualZone !== '';

  let zones: string[];
  let source: LocationSource;
  if (state && codeState && state === codeState) {
    // Same state: the area code may narrow it (Florida peninsula, El Paso…).
    zones = unique(codeZones);
    source = codeZones.length < stateZones.length ? 'area_code' : 'state';
  } else if (state && codeState) {
    zones = candidates;
    source = 'state_and_area_code';
  } else if (state) {
    zones = unique(stateZones);
    source = 'state';
  } else {
    zones = unique(codeZones);
    source = 'area_code';
  }
  return { ok: true, zones, states, source, mismatch, candidates, manualIgnored };
}
